import { Component, OnInit, output, input, inject } from '@angular/core';
import { FormControl, Validators, FormsModule } from '@angular/forms';

import { AuthenticationToken, RegistrationStage } from '@ap/browser-authenticator';
import { ApContentService, ApContentPipe } from '@ap/content';
import { AuthenticationRequest } from '@ap/fido-uaf-frontend-client';
import { ErrorCode } from '@ap/interface-apic-authenticatie-fido';
import { markFormControlAsDone } from '@ap/form';

import { PendingToken, SignError } from '../../sign.model';
import { ValidationResult, ValidationResultType } from '../../validation/validation.model';

import { ApBrowserService } from './browser.service';
import content from './browser.content.json';
import { ApBrowserSelectorComponent } from './selector/selector.component';
import { ApBrowserPincodeComponent } from './pincode/pincode.component';

/**
 * @ngdoc component
 * @name ApBrowserComponent
 */
@Component({
    selector: 'ap-browser',
    standalone: true,
    templateUrl: 'browser.component.html',
    imports: [FormsModule, ApBrowserSelectorComponent, ApBrowserPincodeComponent, ApContentPipe]
})
export class ApBrowserComponent implements OnInit {
    private readonly browserService = inject(ApBrowserService);
    private readonly contentService = inject(ApContentService);

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Required - Text to show on submit button
     */
    public readonly submitText = input.required<string>();

    /**
     * Required - Authentication request
     */
    public readonly authenticationRequest = input.required<AuthenticationRequest>();

    /**
     * Required - The keyId is used to re-initialize after a failed attempt
     */
    public readonly keyId = input.required<string>();

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Passed to `ap-browser-pincode` component.
     * Show button if user has forgotten credentials.
     * Defaults to false
     */
    public readonly showLostCredentials = input(false);

    /**
     * Is user anonymous.
     * Defaults to false
     */
    public readonly anonymous = input(false);

    /**
     * Are pending tokens allowed?
     * Defaults to false
     */
    public readonly allowPendingTokens = input(false);

    /**
     * Is signing implicit?
     * The header for selecting from a multi-user browser registration will be hidden when set to `true`
     * Defaults to false
     */
    public readonly implicitSigning = input(false);

    /**
     * Emits event when user cancels sign process
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when signing fails
     */
    public signError = output<SignError>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<string>();

    /**
     * Emits event when a restart of the signing process is triggered
     */
    public restart = output<string>();
    public signedWithPendingToken = output<PendingToken>();
    public signedWithInvalidCredentials = output<string>();
    public lostCredentials = output<string>();

    public signResponse: FormControl<string>;
    public canSign = true;
    public tokens: AuthenticationToken[];

    public selectedToken: AuthenticationToken;

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.signResponse = new FormControl(null, [Validators.required, Validators.minLength(5)]);

        this.browserService.getTokensForPolicy(this.authenticationRequest().policy, this.allowPendingTokens()).subscribe((tokens) => {
            this.tokens = tokens;

            const keyId = this.keyId();
            if (keyId) {
                this.selectToken(keyId);
            } else if (this.tokens.length === 1) {
                this.selectToken(this.tokens[0].keychainId);
            }
        });
    }

    public selectToken(id: string): void {
        this.selectedToken = this.tokens.find((token) => token.keychainId === id);
    }

    public cancel(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.canceled.emit();
    }

    public onLostCredentials(): void {
        this.lostCredentials.emit(this.selectedToken.keychainId);
    }

    public sign(): void {
        const { canSign, signResponse } = this;
        canSign && markFormControlAsDone(signResponse);
        if (canSign && signResponse.valid) {
            this.canSign = false;
            this.browserService
                .validateToken(this.authenticationRequest(), this.selectedToken.keychainId, signResponse.value, this.actionId())
                .subscribe((result) => {
                    if (result.reason === ErrorCode.INVALID_CREDENTIALS) {
                        this.signedWithInvalidCredentials.emit(this.selectedToken.keychainId);
                    }
                    this.notifyResult(result);
                });
        }
    }

    private notifyResult(validationResult: ValidationResult): void {
        if (validationResult.resultType === ValidationResultType.DONE && this.selectedToken.stage !== RegistrationStage.VERIFIED) {
            this.signedWithPendingToken.emit({
                keychainId: this.selectedToken.keychainId,
                pincode: this.signResponse.value
            });
        } else if (validationResult.resultType === ValidationResultType.DONE) {
            // TODO: The 'emit' function requires a mandatory void argument
            this.done.emit();
        } else {
            this.signError.emit({ key: validationResult.reason, type: 'error' });
            this.signResponse.reset();

            if (validationResult.resultType === ValidationResultType.PROCESS_RETRY) {
                this.retry.emit(this.selectedToken.keychainId);
            } else if (validationResult.resultType === ValidationResultType.COMPONENT_RETRY) {
                this.restart.emit(this.selectedToken.keychainId);
            }
        }
    }
}
